import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { AssessmentItem, DayData, SourceKey, TrainingItem } from './types';
import { SOURCE_LOGO } from './visual';

interface Props {
  date: string;
  day: DayData;
  enabledSources: SourceKey[];
  isToday?: boolean;
  focused?: boolean;
  isCurrentMonth?: boolean;
  onClick?: () => void;
}

function bucketFor(item: TrainingItem | AssessmentItem): SourceKey {
  if (item.kind === 'hawkin_test') return 'hawkin';
  if (item.kind === 'catapult_session') return 'catapult';
  if (item.kind === 'garmin_daily') return 'garmin';
  // terra_activity: bucket by the actual provider so each brand gets its
  // own logo + filter chip (rather than rolling up under a generic key).
  return item.source as SourceKey;
}

function activeBuckets(day: DayData, enabled: SourceKey[]): SourceKey[] {
  const set = new Set<SourceKey>();
  for (const it of day.training) set.add(bucketFor(it));
  for (const it of day.assessment) set.add(bucketFor(it));
  if (day.recovery.sleep?.source === 'whoop' || day.recovery.hrv?.source === 'whoop') {
    set.add('whoop');
  }
  const enabledSet = new Set(enabled);
  return Array.from(set).filter((s) => enabledSet.has(s));
}

export function MonthDayCell({
  date, day, enabledSources, isToday, focused, isCurrentMonth = true, onClick,
}: Props) {
  const { isDark } = useTheme();
  const [, , dd] = date.split('-');
  const buckets = activeBuckets(day, enabledSources);

  const cellStyle: React.CSSProperties = {
    ...(focused ? {
      // inset shadow — outset is clipped by the grid's overflow-hidden
      // ancestor on the leftmost / rightmost focused cells.
      boxShadow: 'inset 0 0 0 2px var(--text-primary)',
      position: 'relative',
      zIndex: 1,
    } : {}),
    ...(!isCurrentMonth ? { opacity: 0.4 } : {}),
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-focused={focused ? 'true' : undefined}
      data-today={isToday ? 'true' : undefined}
      data-current-month={isCurrentMonth ? 'true' : 'false'}
      style={cellStyle}
      className="w-full h-full p-2 text-left flex flex-col gap-1 border border-[var(--card-border)]"
    >
      <div className="flex items-start">
        {isToday ? (
          <span className="inline-flex w-6 h-6 items-center justify-center text-xs font-semibold text-white bg-red-600 rounded-full">
            {Number(dd)}
          </span>
        ) : (
          <span className="text-sm cgm-text-primary">{Number(dd)}</span>
        )}
      </div>
      <div className="flex-1" />
      {buckets.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {buckets.map((b) => {
            const logo = SOURCE_LOGO[b];
            return (
              <img
                key={b}
                src={isDark ? logo.dark : logo.light}
                alt={`${b} logo`}
                data-testid={`source-logo-${b}`}
                className="w-3.5 h-3.5 object-contain"
              />
            );
          })}
        </div>
      )}
    </button>
  );
}
